/**
 * useConsents Hook
 *
 * Provides consent state management for the authenticated user:
 * - Fetches current consent state from the API
 * - Updates consent decisions (give/withdraw) with versioned text
 * - Fetches the append-only consent audit history
 *
 * Consent text version and full text are sourced from the i18n resources
 * and passed to the API so the backend stores the exact wording shown to the user.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createApiClient,
  type ConsentStateEntry,
  type ConsentHistoryEntry,
  type ConsentsResponse,
  type ConsentHistoryResponse,
  type UpdateConsentsRequest,
} from "@floline/api-client";
import { API_URL } from "@/env";
import { insightsKeys } from "./use-insights";
import { dismissedInsightsKeys } from "./use-dismissed-insights";

export const consentKeys = {
  all: ["consents"] as const,
  history: ["consents", "history"] as const,
};

const apiClient = createApiClient(API_URL);

async function fetchConsents(): Promise<ConsentsResponse> {
  const response = await apiClient.getConsents();
  return response.data;
}

async function fetchConsentHistory(): Promise<ConsentHistoryResponse> {
  const response = await apiClient.getConsentHistory();
  return response.data;
}

/**
 * The consent text version identifier.
 *
 * Bump this whenever the legally-significant consent wording changes
 * in the i18n resources. The version is stored alongside each consent
 * event so we can prove which text the user agreed to.
 */
export const CONSENT_TEXT_VERSION = "2026-02-21-v1";

export interface UseConsentsOptions {
  /** Whether to fetch consents on mount. Defaults to true. */
  enabled?: boolean;
}

export function useConsents(options: UseConsentsOptions = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  const consentsQuery = useQuery({
    queryKey: consentKeys.all,
    queryFn: fetchConsents,
    staleTime: 2 * 60 * 1000,
    enabled,
  });

  const historyQuery = useQuery({
    queryKey: consentKeys.history,
    queryFn: fetchConsentHistory,
    staleTime: 60 * 1000,
    enabled: false, // Only fetched on demand
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateConsentsRequest) => {
      const response = await apiClient.updateConsents(data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(consentKeys.all, data);
      // Invalidate history since a new event was recorded
      void queryClient.invalidateQueries({ queryKey: consentKeys.history });

      const aiInsightsDecision = variables.decisions.ai_insights;
      if (aiInsightsDecision) {
        // Prevent stale insights from lingering after consent toggles.
        queryClient.removeQueries({ queryKey: insightsKeys.all });
        queryClient.removeQueries({ queryKey: dismissedInsightsKeys.all });
      }
    },
  });

  return {
    /** Current consent state entries */
    consents: (consentsQuery.data?.consents ?? []) as ConsentStateEntry[],
    isLoading: consentsQuery.isLoading,
    isFetched: consentsQuery.isFetched,
    isError: consentsQuery.isError,
    error: consentsQuery.error,
    refetch: consentsQuery.refetch,

    /** Update consent decisions */
    updateConsents: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,

    /** Consent audit history (fetched on demand) */
    history: (historyQuery.data?.events ?? []) as ConsentHistoryEntry[],
    isLoadingHistory: historyQuery.isLoading,
    isFetchedHistory: historyQuery.isFetched,
    fetchHistory: historyQuery.refetch,
  };
}
